import { Component, ErrorInfo, ReactNode } from 'react';
import { reportErrorFromBoundary } from '../utils/errorHandler';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
    onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            error: null
        };
    }

    static getDerivedStateFromError(error: Error): State {
        return {
            hasError: true,
            error
        };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        console.error('ErrorBoundary caught an error:', error, errorInfo);
        try {
            reportErrorFromBoundary(error, errorInfo.componentStack ?? undefined);
        } catch {
            // Error reporting must never interfere with error recovery
        }
        this.props.onError?.(error, errorInfo);
    }

    render(): ReactNode {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <div className="flex items-center justify-center p-4">
                    <div className="bg-red-50 border-2 border-red-300 rounded-lg px-4 py-3 max-w-md">
                        <h3 className="text-red-800 font-semibold mb-2">Something went wrong</h3>
                        <p className="text-red-600 text-sm">
                            {this.state.error?.message || 'An unexpected error occurred'}
                        </p>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
